/**
 * English strings for the profile screens.
 *
 * Written out in full and derived from nothing — see en/index.ts. Typed as
 * a complete slice, so a key missing here is a build error rather than a
 * screen that quietly renders Bosnian.
 *
 * Same audience as the sign-in screens: somebody in the building who does not
 * read the local language. So "card" rather than "badge" — the thing in their
 * hand is a laminated printed card — and "supervisor" for the person upstairs
 * who issues activation codes, which is what the plant calls them in English.
 */

import type { ProfileSlice } from '../bs/profile'

export const profile: ProfileSlice = {
  // ---- The page, and words it shares with the screens below --------
  'profile.title': 'My account',
  'profile.subtitle': 'Contact details, password and account protection.',
  'profile.attribution': 'KaProLean — software by:',
  'profile.cancel': 'Cancel',
  'profile.backToLogin': 'Back to signing in',

  'profile.verified.success': 'Your e-mail address has been confirmed.',
  'profile.verified.failed': 'That confirmation link is not valid, or it has expired.',

  // ---- Identity ----------------------------------------------------
  'profile.identity.title': 'Employee details',
  'profile.identity.name': 'Full name',
  'profile.identity.employeeCode': 'Employee code',
  'profile.identity.role': 'Role',
  'profile.identity.plant': 'Plant',
  'profile.identity.lastLogin': 'Last signed in',
  'profile.identity.note':
    'An administrator changes these details. If something is wrong, tell your supervisor.',

  // ---- Contact -----------------------------------------------------
  'profile.contact.title': 'Contact',
  'profile.contact.email': 'E-mail',
  'profile.contact.email.hint':
    'Not required. Most workers have no address — messages inside the app always arrive.',
  'profile.contact.email.placeholder': 'first.last@company.com',
  'profile.contact.verified': 'Address confirmed',
  'profile.contact.unverified': 'Address not confirmed',
  'profile.contact.sendLink': 'Send the link',
  'profile.contact.sendFailed': 'That did not send.',
  'profile.contact.phone': 'Phone',
  'profile.contact.phone.placeholder': 'e.g. 063 123 456',
  'profile.contact.notify': 'Send messages to my e-mail as well',
  'profile.contact.notify.hint': 'Only works once the address is confirmed.',
  'profile.contact.save': 'Save',

  // ---- My own QR card ----------------------------------------------
  'profile.badge.title': 'My QR card',
  'profile.badge.intro':
    'Instead of typing your code and password, scan your card with your phone and you are signed in. Useful when you are standing at a machine with gloves on.',
  'profile.badge.none': 'You have no card yet.',
  'profile.badge.issued':
    'You already have a card. The code cannot be shown again; if you need a new one, the old card stops working.',
  'profile.badge.issuedAt':
    'You already have a card — {date}. The code cannot be shown again; if you need a new one, the old card stops working.',
  'profile.badge.managerWarning':
    'Your account has extended rights. If you issue a card, switch two-factor protection on as well — for an account like this a card on its own is not enough.',
  'profile.badge.issue': 'Issue a card',
  'profile.badge.issueNew': 'Issue a new card',
  'profile.badge.revoke': 'Cancel the card',
  'profile.badge.reissueConfirm':
    'You already have a card.\n\nIssuing a new one stops the card you are carrying from working, straight away. Continue?',
  'profile.badge.revokeConfirm':
    'Cancel the card?\n\nThe card stops working straight away. Signing in with your code and password still works.',
  'profile.badge.issueFailed': 'The card was not issued.',
  'profile.badge.revokeFailed': 'The card was not cancelled.',
  'profile.badge.printHint':
    'Print it now and laminate it. The code is shown this once — if you close the page before printing, issue a new card.',
  'profile.badge.print': 'Print the card',
  'profile.badge.done': 'Done',

  // ---- Password ----------------------------------------------------
  'profile.password.title': 'Change your password',
  'profile.password.current': 'Current password',
  'profile.password.new': 'New password',
  'profile.password.new.hint': 'At least 8 characters.',
  'profile.password.confirm': 'Repeat the password',
  'profile.password.confirmNew': 'Repeat the new password',
  'profile.password.mismatch': 'The two passwords are not the same.',
  'profile.password.otherSessionsNote':
    'Once it is changed, every other sign-in to your account ends — this one stays.',
  'profile.password.submit': 'Change password',

  // ---- Two-factor --------------------------------------------------
  'profile.twoFactor.title': 'Two-factor protection',
  'profile.twoFactor.on': 'On',
  'profile.twoFactor.off': 'Off',
  'profile.twoFactor.description':
    'On top of the password it asks for the six-digit code from the app on your phone (Google Authenticator, Microsoft Authenticator, Aegis…). Recommended for supervisors and administrators.',
  'profile.twoFactor.enable': 'Switch protection on',
  'profile.twoFactor.disable': 'Switch off',
  'profile.twoFactor.newRecoveryCodes': 'New backup codes',
  'profile.twoFactor.beginFailed': 'That did not start.',
  'profile.twoFactor.disableTitle': 'Switch two-factor protection off?',
  'profile.twoFactor.disableExplain':
    'From now on the account will be protected by the password alone. Confirm with your password that this is what you want.',
  'profile.twoFactor.password': 'Password',

  'profile.twoFactor.setup.title': 'Switching two-factor protection on',
  'profile.twoFactor.setup.confirm': 'Confirm',
  'profile.twoFactor.setup.step1': '1. Scan the code',
  'profile.twoFactor.setup.step1.hint': 'Open the code app on your phone and scan this:',
  'profile.twoFactor.setup.qrAlt': 'QR code for two-factor protection',
  'profile.twoFactor.setup.step2': '2. Or type the key in by hand',
  'profile.twoFactor.setup.step2.hint': 'If the camera cannot catch the code:',
  'profile.twoFactor.setup.step3': '3. Enter the code from the app',
  'profile.twoFactor.setup.code': 'Six-digit code',

  // ---- Recovery codes ----------------------------------------------
  'profile.recovery.title': 'Backup codes',
  'profile.recovery.intro':
    'Keep these codes somewhere safe — each one works once, and they are how you get into the account if you lose your phone.',
  'profile.recovery.note':
    'You can look at the codes again on this page while you are signed in.',
  'profile.recovery.copy': 'Copy the codes',
  'profile.recovery.copied': 'The codes have been copied.',

  // ---- Signed-in devices -------------------------------------------
  'profile.sessions.title': 'Signed-in devices',
  'profile.sessions.note':
    'You can be signed in on several devices at once. If you have lost your phone, or stayed signed in on a shared computer, sign that device out here.',
  'profile.sessions.revokeOthers': 'Sign the others out',
  'profile.sessions.loadFailed': 'The list of devices could not be loaded.',
  'profile.sessions.empty': 'No sign-ins at the moment.',
  'profile.sessions.thisDevice': 'this device',
  'profile.sessions.lastActivity': 'Last used {when}',
  'profile.sessions.neverUsed': 'Not used yet',
  'profile.sessions.logout': 'Sign out',
  'profile.sessions.logoutSelf': 'Sign myself out',
  'profile.sessions.revokeAria': 'Sign out {device}',
  'profile.sessions.logoutFailed': 'Signing out failed.',

  // ---- Forgotten password ------------------------------------------
  'profile.forgot.title': 'Forgotten password',
  'profile.forgot.intro':
    'Type in your employee code. If the account has a confirmed e-mail address, we will send you a link.',
  'profile.forgot.identifier': 'Employee code or e-mail',
  'profile.forgot.identifier.hint': 'Either one — whichever you have to hand.',
  'profile.forgot.identifier.placeholder': 'e.g. 1042',
  'profile.forgot.submit': 'Send the link',
  'profile.forgot.sendFailed': 'That did not send. Check your connection and try again.',
  'profile.forgot.sent.title': 'Check your e-mail',
  'profile.forgot.sent.body':
    'If the account exists and has a confirmed e-mail address, we have sent a link for setting a new password. Look in the junk folder too.',
  'profile.forgot.sent.expiry': 'The link lasts an hour and can be used only once.',
  'profile.forgot.sent.noMail':
    'Nothing arrived? If there is no confirmed e-mail address on your account — and most workers have none — ask your supervisor. You will be given an activation code and set the new password yourself.',
  'profile.forgot.noEmail.title': 'No e-mail address?',
  'profile.forgot.noEmail.body':
    'Ask your supervisor — you will be given an activation code, sign in with it and set your password right away.',

  // ---- Setting a password from a mailed link ------------------------
  'profile.reset.welcome': 'Welcome',
  'profile.reset.title': 'New password',
  'profile.reset.intro.invitation':
    'Set a password for account {code}. That code is what you sign in with from now on — not your e-mail address.',
  'profile.reset.intro.reset':
    'You are setting a new password for account {code}. Only you will know it.',
  'profile.reset.submit': 'Save the new password',
  'profile.reset.allSessionsNote': 'Once it is changed, every sign-in on every device ends.',
  'profile.reset.success': 'Your password has been changed. Sign in with the new one.',
  'profile.reset.failed':
    'The password was not changed. Check your connection and try again.',
  'profile.reset.badLink.title': 'That link is not right',
  'profile.reset.badLink.body':
    'This link is incomplete — usually because it was broken in half by the mail. Ask for a new one and open it with a single tap from the message.',
  'profile.reset.badLink.action': 'Ask for a new link',

  // ---- The forced first password -----------------------------------
  'profile.setPassword.title': 'Set your own password',
  'profile.setPassword.intro':
    'You signed in with an activation code your supervisor read out to you. Set a password now that only you know — the code stops working after that.',
  'profile.setPassword.submit': 'Save the password',
  'profile.setPassword.success': 'Your password is set. Welcome.',
  'profile.setPassword.failed': 'The password was not set.',
  'profile.setPassword.signedInAs': 'You are signed in as {name} ({code}).',
  'profile.setPassword.notMe': 'This is not me — sign me out',

  // ---- Where a scanned card lands ----------------------------------
  'profile.badgeLogin.working': 'Signing in with your card…',
  'profile.badgeLogin.recognising': 'Reading your card.',
  'profile.badgeLogin.failed': 'Signing in with the card failed.',
  'profile.badgeLogin.lostCard':
    'If the card is damaged or lost, your supervisor can issue you a new one.',
  'profile.badgeLogin.usePassword': 'Sign in with code and password',
  'profile.twoFactor.required':
    'Your company requires two-factor protection for administrators. Until you turn it on, administration is locked.',
}
